import { COLORS } from '@/config/colors';
import { circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

interface BlastPalette {
  ring: number;
  fill: number;
  /** Body opacity — player blasts stay see-through so shots read under them */
  fillAlpha: number;
  inner: number;
  hot: number;
  spark: number;
}

function rgba(color: number, a: number): string {
  return `rgba(${(color >> 16) & 0xff}, ${(color >> 8) & 0xff}, ${color & 0xff}, ${a})`;
}

/** Expanding ring + fading core + sparks; frame t runs 0..1 over 6 frames. */
function drawBlast(ctx: CanvasRenderingContext2D, w: number, frame: number, p: BlastPalette): void {
  const c = w / 2;
  const t = frame / 5;
  const R = (w / 2 - 6) * (0.3 + t * 0.7);

  // Outer ring — thins and fades as it grows
  ctx.beginPath();
  ctx.arc(c, c, R, 0, Math.PI * 2);
  ctx.strokeStyle = rgba(p.ring, 1 - t * 0.8);
  ctx.lineWidth = 10 * (1 - t * 0.7);
  ctx.stroke();

  // Inner body — solid early, then fades
  if (t < 0.7) {
    circle(ctx, c, c, R * (0.75 - t * 0.5));
    ctx.fillStyle = rgba(p.fill, p.fillAlpha);
    ctx.fill();
    circle(ctx, c, c, R * (0.45 - t * 0.35));
    ctx.fillStyle = rgba(p.inner, Math.min(1, p.fillAlpha + 0.3));
    ctx.fill();
  }
  if (t < 0.4) {
    circle(ctx, c, c, R * 0.2);
    ctx.fillStyle = rgba(p.hot, 0.95);
    ctx.fill();
  }

  // Scattering sparks
  ctx.fillStyle = rgba(p.spark, 1 - t);
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i + 0.5;
    circle(ctx, c + Math.cos(a) * R * 1.05, c + Math.sin(a) * R * 1.05, 3.5 * (1 - t * 0.6));
    ctx.fill();
  }
}

/** Medium explosion, warm palette — boss/ship destruction (enemy family). */
export const fxExplosionM: ProceduralDef = {
  key: 'fx/explosion-m',
  width: 112,
  height: 112,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 6,
  draw(ctx, w, h, frame) {
    drawBlast(ctx, w, frame, {
      ring: COLORS.dangerOrange,
      fill: COLORS.dangerOrange,
      fillAlpha: 1,
      inner: shade(COLORS.playerYellow, 1.2),
      hot: 0xfffcf0,
      spark: COLORS.playerYellow,
    });
    void h;
  },
};

/** Player-caused blast (AoE impact, kill) — cyan/white, see-through (E4). */
export const fxExplosionPlayer: ProceduralDef = {
  key: 'fx/explosion-player',
  width: 112,
  height: 112,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 6,
  draw(ctx, w, h, frame) {
    drawBlast(ctx, w, frame, {
      ring: COLORS.fxBlastRing,
      fill: COLORS.fxBlastRing,
      fillAlpha: 0.4,
      inner: COLORS.fxBlastCore,
      hot: COLORS.fxBlastHot,
      spark: COLORS.fxBlastCore,
    });
    void h;
  },
};

/** Ship hit: thin red ring expanding off the hull, hollow center (E4). */
export const fxHurtRing: ProceduralDef = {
  key: 'fx/hurt-ring',
  width: 112,
  height: 112,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 5,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const t = frame / 4;
    const R = (w / 2 - 6) * (0.4 + t * 0.6);
    ctx.beginPath();
    ctx.arc(c, c, R, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(COLORS.fxHurtRing, 0.9 * (1 - t * 0.85));
    ctx.lineWidth = 6 * (1 - t * 0.6);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(c, c, R - 5, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(0xffffff, 0.5 * (1 - t));
    ctx.lineWidth = 2;
    ctx.stroke();
    void h;
  },
};
